import { ApiError, isApiError, messageKey, toApiError } from "../data/errors";

/**
 * 2F: the codes the combine endpoints answer with (not-duplicate, scan, candidates, preview, combine, and the removed
 * contact's old link). `CONTACT_CHANGED` is the contact page's conflict (its own notice, `conflictOf`); `CONTACT_NOT_FOUND`
 * is listed below already.
 */
export const MERGE_ERROR_CODES = [
  "COMBINE_NOT_ALLOWED",
  "DUPLICATES_FILTER_NOT_ALLOWED",
  "SCAN_RUNNING",
  "CONTACT_MERGED",
  "CHANGES_WAITING",
  "SAME_CONTACT",
] as const;

/** Every refusal code the contacts endpoints answer with ({message, code}); each has `contacts.errors.<code lower-case>`. */
export const CONTACT_ERROR_CODES = [
  "SELECTION_EMPTY",
  "CONFIRM_COUNT_MISMATCH",
  "PROJECTS_FILTER_MANAGERS_ONLY",
  "CONTACT_INFO_FILTER_NOT_ALLOWED",
  "HISTORY_FILTER_NOT_ALLOWED",
  "OVER_CONTACT_LIMIT",
  "CATEGORY_REACH_NOT_ALLOWED",
  // The contact page (2C): edit and delete refusals.
  "UPDATE_NOT_ALLOWED",
  "CONTACT_INFO_NOT_ALLOWED",
  "PARTS_DELETE_NOT_ALLOWED",
  "CONTACT_NOT_FOUND",
  "DELETE_NOT_ALLOWED",
  // 2E: `possible_duplicate=1` without `can.see_duplicates` (list, counts, select-all preview, bulk); 2F: combining.
  ...MERGE_ERROR_CODES,
] as const;

const KNOWN = new Set<string>(CONTACT_ERROR_CODES);

/**
 * Translation key (under `next.`) for a contacts error. A known code gets its own sentence; a policy 403
 * (no code) gets "You don't have permission"; any other 4xx gets the generic line (the server's text is English,
 * never shown in the Hebrew UI); network / 5xx fall back to `messageKey`.
 */
export const errorMessageKey = (e: ApiError): string => {
  if (e.code && KNOWN.has(e.code)) return `contacts.errors.${e.code.toLowerCase()}`;
  if (e.status === 403 && !e.code) return "contacts.errors.forbidden";
  if (e.kind === "client") return "errors.generic";
  return messageKey(e) ?? "errors.generic";
};

/** The sentence to show for any thrown value. */
export const errorText = (err: unknown, t: (key: string) => string): string => {
  const e = isApiError(err) ? err : toApiError(err);
  return t(errorMessageKey(e));
};
